/**
 * CascadeTestModeBanner — owner-side toggle for the contractor cascade test mode.
 *
 * When ON, every cascade email (award, kickoff, cadence, schedule-shift) is
 * rerouted to the owner's `cascade_test_email` with a `[TEST → contractor@x]`
 * subject prefix so the owner can preview the full sequence without spamming
 * real contractors.
 *
 * Surfaces in:
 *   - Project Lifecycle page (top banner)
 *   - Settings page (full card)
 */
import React, { useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ShieldAlert, ShieldCheck, Mail, Save } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';

export default function CascadeTestModeBanner({ user, compact = false }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(true);
  const [testEmail, setTestEmail] = useState('');
  const [fallbackEmail, setFallbackEmail] = useState('');

  useEffect(() => {
    if (!user?.id) return;
    let alive = true;
    (async () => {
      try {
        const res = await api.get(`/users/${user.id}/cascade-test-mode`);
        if (!alive) return;
        setEnabled(res.data.cascade_test_mode);
        setTestEmail(res.data.cascade_test_email || '');
        setFallbackEmail(res.data.fallback_email || '');
      } catch (e) {
        toast.error('Could not load cascade settings');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [user]);

  const save = async (nextEnabled = enabled, nextEmail = testEmail) => {
    if (!user?.id) return;
    setSaving(true);
    try {
      const res = await api.patch(`/users/${user.id}/cascade-test-mode`, {
        cascade_test_mode: nextEnabled,
        cascade_test_email: nextEmail,
      });
      setEnabled(res.data.cascade_test_mode);
      setTestEmail(res.data.cascade_test_email || '');
      toast.success(
        nextEnabled
          ? `Test mode ON — emails reroute to ${res.data.cascade_test_email}`
          : 'Test mode OFF — emails will go to real contractors'
      );
    } catch (e) {
      toast.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  const onToggle = (checked) => {
    setEnabled(checked);
    save(checked, testEmail);
  };

  if (loading) {
    return (
      <Card className="p-3 bg-white border-slate-200 text-xs text-slate-500" data-testid="cascade-test-mode-loading">
        Loading cascade settings…
      </Card>
    );
  }

  // Compact banner (used inside Project Lifecycle page)
  if (compact) {
    const tone = enabled ? 'bg-amber-50 border-amber-300' : 'bg-emerald-50 border-emerald-300';
    return (
      <Card
        className={`p-3 ${tone} border flex items-center gap-3 mb-4`}
        data-testid="cascade-test-mode-banner"
      >
        {enabled ? (
          <ShieldAlert className="w-5 h-5 text-amber-700 flex-shrink-0" />
        ) : (
          <ShieldCheck className="w-5 h-5 text-emerald-700 flex-shrink-0" />
        )}
        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold text-slate-900" data-testid="cascade-test-mode-state-label">
            {enabled ? 'Test Mode: ON' : 'Test Mode: OFF — Live Sends'}
          </p>
          <p className="text-[11px] text-slate-600 truncate">
            {enabled
              ? <>Cascade emails reroute to <strong>{testEmail || fallbackEmail}</strong>. Real contractors will <strong>not</strong> be emailed.</>
              : <>Cascade emails go to <strong>real contractor inboxes</strong>. Use with care.</>}
          </p>
        </div>
        <Switch
          checked={enabled}
          onCheckedChange={onToggle}
          disabled={saving}
          data-testid="cascade-test-mode-toggle"
        />
      </Card>
    );
  }

  // Full card (used in Settings page)
  return (
    <Card className="p-6 bg-white border-slate-200" data-testid="cascade-test-mode-card">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            {enabled ? <ShieldAlert className="w-5 h-5 text-amber-600" /> : <ShieldCheck className="w-5 h-5 text-emerald-600" />}
            <h3 className="text-base font-bold text-slate-900">Contractor Cascade · Test Mode</h3>
            <Badge
              variant="outline"
              className={enabled ? 'border-amber-400 text-amber-700 bg-amber-50' : 'border-emerald-400 text-emerald-700 bg-emerald-50'}
              data-testid="cascade-test-mode-badge"
            >
              {enabled ? 'ON · Safe' : 'OFF · Live'}
            </Badge>
          </div>
          <p className="text-xs text-slate-600 max-w-2xl">
            When ON, <strong>every outgoing email to anyone other than you</strong> — contractors <em>and</em> your team
            members (award/kickoff, portal access, compliance, cadence pings, <strong>plus budget &amp; schedule alerts</strong>) —
            reroutes to your test inbox with a <code className="px-1 py-0.5 bg-slate-100 rounded">[TEST → recipient@email]</code>
            prefix and is capped to a few per day. Great for previewing a demo; <strong>turn it OFF for live use</strong> so your
            team and contractors actually receive their emails.
          </p>
        </div>
        <Switch
          checked={enabled}
          onCheckedChange={onToggle}
          disabled={saving}
          data-testid="cascade-test-mode-toggle-settings"
        />
      </div>

      <div className="border-t border-slate-200 pt-4 mt-2">
        <Label htmlFor="cascade-test-email" className="text-xs font-semibold text-slate-700 mb-1 block">
          Test Inbox
        </Label>
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Mail className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              id="cascade-test-email"
              type="email"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              placeholder={fallbackEmail || 'you@yourcompany.com'}
              disabled={saving}
              className="pl-8 h-9 text-sm"
              data-testid="cascade-test-email-input"
            />
          </div>
          <Button
            size="sm"
            onClick={() => save(enabled, testEmail)}
            disabled={saving}
            data-testid="cascade-test-email-save"
          >
            <Save className="w-3.5 h-3.5 mr-1" />
            Save
          </Button>
        </div>
        <p className="text-[11px] text-slate-500 mt-1.5">
          All cascade emails reroute here while Test Mode is ON. Leave blank to fall back to your account email ({fallbackEmail}).
        </p>
      </div>

      {!enabled && (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md text-xs text-red-900" data-testid="cascade-live-warning">
          <strong>⚠️ Test Mode is OFF.</strong> The next "Award Contract" click will send real emails to real contractors.
          Make sure your cascade copy, attachments, and contact info are ready.
        </div>
      )}
    </Card>
  );
}
